<template>
    <div class="fb">
        <header class="hd">
            <h1>Fayllar</h1>
            <div class="tabs">
                <button v-for="t in ['image', 'media', 'file']" :key="t" class="tab" :class="{ active: kind === t }"
                    @click="switchKind(t)">{{ TR[t] }}</button>
                    <!-- @click="switchKind(t)">{{ t[0].toUpperCase() + t.slice(1) }}</button> -->
            </div>
            <div class="spacer" />
            <div class="toolbar">
                <input v-model="q" type="search" :placeholder="TR.search" @input="debouncedSearch" />
                <button class="ghost" @click="openUrlDlg">{{ TR.insert_url }}</button>
                <input ref="fileInput" type="file" multiple hidden @change="onFilePicked">
                <button class="primary" @click="fileInput?.click()">{{ TR.upload }}</button>
            </div>
        </header>

        <main class="fb-main">
            <div class="dropzone" :class="{ drag: isDrag }" @dragenter.prevent="isDrag = true"
                @dragover.prevent="isDrag = true" @dragleave.prevent="isDrag = false" @drop.prevent="onDrop">
                Yükləmək üçün faylları buraya daşı
            </div>

            <div v-if="!items.length && !loading" class="empty">Siyahı boşdur.</div>

            <div v-else class="grid" aria-live="polite">
                <div v-for="it in items" :key="it.id" class="card" @dblclick="select(it)">
                    <img v-if="thumb(it)" :src="thumb(it)" class="thumb" :alt="it.title || ''" />
                    <div v-else class="thumb ph">{{ icon(it.mime) }}</div>

                    <div class="meta">
                        <div class="title" :title="it.title || ''">{{ it.title || '(untitled)' }}</div>
                        <div class="muted">{{ sizeFmt(it.size) }}<template v-if="it.size && it.mime"> • </template>{{
                            it.mime || ''
                            }}</div>
                        <div class="d-flex justify-content-between">
                            <button class="primary" @click="select(it)">{{ TR.select }}</button>
                            <button @click="openEdit(it)">{{ TR.edit }}</button>
                            <button class="danger" @click="del(it)">{{ TR.remove }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <footer class="ft">
            <div class="muted">{{ total }} nəticə</div>
            <div class="pagination">
                <button :disabled="page <= 1" @click="prev">{{ TR.previous }}</button>
                <span class="pill">{{ page }}</span>
                <button :disabled="disableNext" @click="next">{{ TR.next }}</button>
            </div>
        </footer>

        <!-- Edit Dialog -->
        <div v-if="dlgEdit" class="modal">
            <div class="panel">
                <div class="dlg-hd">{{ TR.edit }}</div>
                <div class="dlg-bd">
                    <div class="field">
                        <label>{{ TR.title }}</label>
                        <input v-model="editTitle" type="text" />
                        <div class="help">Redaktə zamanı faylın adınıda dəyişmiş olacaqsınız.</div>
                    </div>
                    <div class="field">
                        <label>İlkin baxış</label>
                        <div class="help" v-if="!current">Fayl yoxdur</div>
                        <div v-else class="preview">
                            <img v-if="(current.mime || '').startsWith('image/')" :src="current.url"
                                style="max-width:50%;" />
                            <a v-else :href="current.url" target="_blank" rel="noopener">Cari faylı aç</a>
                        </div>
                    </div>
                    <div class="field">
                        <label>Məlumatı dəyiş (Məcburi deyil)</label>
                        <input ref="replaceInput" type="file" />
                    </div>
                </div>
                <div class="dlg-ft">
                    <button @click="closeEdit">{{ TR.cancel }}</button>
                    <button class="primary" @click="saveEdit">{{ TR.save }}</button>
                </div>
            </div>
        </div>

        <!-- URL Dialog -->
        <div v-if="dlgUrl" class="modal">
            <div class="panel">
                <div class="dlg-hd">Xarici URL daxil et</div>
                <div class="dlg-bd">
                    <div class="field">
                        <label>URL</label>
                        <input v-model="urlInput" type="url" placeholder="https://example.com/file.jpg" />
                    </div>
                    <div class="field">
                        <label>{{ TR.title }}</label>
                        <input v-model="urlTitle" type="text" placeholder="My file" />
                    </div>
                    <div class="help">URL seçilmiş tiplərə şamil olacaq (image / media / link): <b>{{ kind }}</b>.</div>
                </div>
                <div class="dlg-ft">
                    <button @click="dlgUrl = false">{{ TR.cancel }}</button>
                    <button class="primary" @click="insertUrl">{{ TR.insert }}</button>
                </div>
            </div>
        </div>

        <div v-if="loading" class="loading">{{ TR.loading }}</div>
    </div>
</template>

<script setup>
import { onMounted, watchEffect, ref, computed, watch } from 'vue';

import { useStore } from '@/system_modules/store';
import { G, ajax } from '@/_common';

const store = useStore();

const TR = ref({});

watchEffect(() => {
    TR.value = store.getTranslation();
});

/** ===== CONFIG: point to your FastAPI ===== */
const API = {
    list: '/v1/file_browser/az/files',                             // GET ?type=&q=&page=&page_size=
    upload: '/v1/file_browser/az/files',                           // POST multipart: file, type
    edit: (id) => `/v1/file_browser/az/files/${encodeURIComponent(id)}`,     // PATCH {title}
    replace: (id) => `/v1/file_browser/az/files/${encodeURIComponent(id)}/replace`, // POST multipart: file
    remove: (id) => `/v1/file_browser/az/files/${encodeURIComponent(id)}`,   // DELETE
}
const PAGE_SIZE = 24
/** ========================================= */

const items = ref([])
const total = ref(0)
const page = ref(1)
const q = ref('')
const loading = ref(false)
const isDrag = ref(false)

const urlInput = ref('')
const urlTitle = ref('')
const dlgUrl = ref(false)

const dlgEdit = ref(false)
const current = ref(null)
const editTitle = ref('')
const replaceInput = ref(null)

const fileInput = ref(null)

let kind = (new URLSearchParams(location.search).get('type') || 'image').toLowerCase()
if (kind === 'link') kind = 'file'

const disableNext = computed(() => items.value.length < PAGE_SIZE && (page.value * PAGE_SIZE) >= total.value)

/** UTIL */
const delay = (ms) => new Promise(r => setTimeout(r, ms))
function icon(mime = '') {
    if (mime.startsWith('image/')) return '🖼️'
    if (mime.startsWith('video/')) return '🎞️'
    if (mime.startsWith('audio/')) return '🎵'
    if (mime.includes('pdf')) return '📕'
    return '📄'
}
function thumb(it) { return it.thumb || (it.mime?.startsWith('image/') ? it.url : '') }
function sizeFmt(n) {
    if (!n && n !== 0) return ''
    const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let x = Number(n)
    while (x >= 1024 && i < u.length - 1) { x /= 1024; i++ }
    return x.toFixed(x < 10 && i > 0 ? 1 : 0) + ' ' + u[i]
}
function apiUrl(base, params) {
    // const u = new URL(base, window.location.origin)
    const u = new URL(base, G.ajaxBaseUrl.default);
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') u.searchParams.set(k, v) })
    return u.toString()
}
function postSelect(it) {
    const payload = { mceAction: 'select', url: it.url, title: it.title || '', type: kind, id: it.id || null, mime: it.mime || null }
        ; (window.opener || window.parent).postMessage(payload, '*')
}

/** DATA */
async function list() {
    loading.value = true
    try {
        const url = apiUrl(API.list, { type: kind, q: q.value.trim(), page: page.value, page_size: PAGE_SIZE });
        // const res = await fetch(url, {
        //     // credentials: 'include',
        //     headers: {
        //     } })
        // if (!res.ok) throw new Error('List failed')
        // const data = await res.json()
        let data = [];
        await ajax({
            url: url,
            success: (e) => {
                data = e.data;
            }
        });

        const arr = Array.isArray(data) ? data : (data.items || [])
        items.value = arr.map(x => ({
            id: x.id ?? x._id ?? x.uuid ?? x.name ?? G.ajaxBaseUrl.default + x.url,
            url: G.ajaxBaseUrl.default + x.url || x.location,
            title: x.title || x.name || x.filename || '',
            mime: x.mime || x.mimetype || '',
            size: x.size ?? null,
            modified: x.modified ?? x.updated_at ?? null,
            thumb: x.thumb || x.thumbnail || null
        })).filter(x => !!x.url);
        total.value = Number((!Array.isArray(data) && (data.total ?? data.count)) ?? items.value.length);
    } finally {
        loading.value = false;
    }
}

/** ACTIONS */
function switchKind(k) { kind = k; page.value = 1; list() }
const debouncedSearch = debounce(() => { page.value = 1; list() }, 300)
function prev() { if (page.value > 1) { page.value--; list() } }
function next() { if (!disableNext.value) { page.value++; list() } }

function onFilePicked(e) {
    if (!e.target.files?.length) return;
    uploadFiles(e.target.files);
    e.target.value = '';
}
async function onDrop(e) {
    isDrag.value = false;
    const files = e.dataTransfer?.files;
    if (files?.length) await uploadFiles(files);
}
async function uploadFiles(fileList) {
    for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file, file.name);
        fd.append('type', kind);
        const url = apiUrl(API.upload, { type: kind, q: q.value.trim(), page: page.value, page_size: PAGE_SIZE });
        // const res = await fetch(url, { method: 'POST', body: fd,
        // // credentials: 'include',
        // headers: {
        //     } })
        // if (!res.ok) console.warn('Upload failed:', file.name)
        await ajax({
            url: url,
            method: 'post',
            data: fd,
            success: () => {
                G.alert.success({msg: TR.value.msg_success + ' ' + file.name, toast: true});
            }
        });
    }
    await list();
}

function openEdit(it) {
    current.value = it;
    editTitle.value = it.title || '';
    dlgEdit.value = true;
}
function closeEdit() {
    current.value = null;
    editTitle.value = '';
    if (replaceInput.value) replaceInput.value.value = '';
    dlgEdit.value = false;
}
async function saveEdit() {
    if (!current.value) return
    // rename/meta
    const url = apiUrl(API.edit(current.value.id), { type: kind, q: q.value.trim(), page: page.value, page_size: PAGE_SIZE })
    // await fetch(url, {
    //     method: 'PATCH',
    //     headers: { 'Content-Type': 'application/json' },
    //     // credentials: 'include',
    //     body: JSON.stringify({ title: editTitle.value }), headers: {
    //         }
    // }).catch(() => { });

    await ajax({
        url: url,
        method: 'patch',
        content_type: 'application/json',
        data: { title: editTitle.value },
        success: () => {
            G.alert.success({msg: TR.value.msg_success, toast: true});
        }
    });

    // binary replace
    const f = replaceInput.value?.files?.[0]
    if (f) {
        const fd = new FormData()
        fd.append('file', f, f.name)
        const url = apiUrl(API.replace(current.value.id), { type: kind, q: q.value.trim(), page: page.value, page_size: PAGE_SIZE })
        // await fetch(url, { method: 'POST', body: fd,
        // // credentials: 'include',
        // headers: {
        //     } }).catch(() => { })
        await ajax({
            url: url,
            method: 'post',
            content_type: 'application/json',
            data: fd,
            success: () => {
                G.alert.success({msg: TR.value.msg_success + ' ' + file.name, toast: true});
            }
        });
    }
    closeEdit();
    await list();
}
async function del(it) {
    if (!confirm(`Delete "${it.title || it.url}"?`)) return
    const url = apiUrl(API.remove(it.id), { type: kind, q: q.value.trim(), page: page.value, page_size: PAGE_SIZE })
    // const res = await fetch(url, { method: 'DELETE',
    // // credentials: 'include',
    // headers: {
    //         } })
    // if (!res.ok) { alert('Delete failed'); return }

    await ajax({
        url: url,
        method: 'delete',
        success: () => {
            G.alert.success({msg: TR.value.msg_remove_success, toast: true});
        }
    });

    await list()
}
function select(it) { postSelect(it) }

function openUrlDlg() { urlInput.value = ''; urlTitle.value = ''; dlgUrl.value = true }
function insertUrl() {
    const u = (urlInput.value || '').trim()
    if (!u) return
    postSelect({ url: u, title: (urlTitle.value || '').trim(), mime: null, id: null })
    dlgUrl.value = false
}

/** helpers */
function debounce(fn, ms = 250) {
    let t
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms) }
}

/** INIT */
onMounted(() => { list() })
</script>

<style scoped>
.fb {
    color: #e5e7eb;
    background: #0f172a;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.hd {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #1f2937;
    position: sticky;
    top: 0;
    background: #0f172a;
    z-index: 2;
}

h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.tabs {
    display: flex;
    gap: 6px;
}

.tab {
    padding: 6px 10px;
    border-radius: 10px;
    border: 1px solid #1f2937;
    background: #111827;
    color: #e5e7eb;
    cursor: pointer;
    font-size: 12px;
}

.tab.active {
    border-color: transparent;
    background: #6366f1;
    color: #fff;
}

.spacer {
    flex: 1;
}

.toolbar {
    display: flex;
    gap: 8px;
    align-items: center;
}

input[type="search"],
input[type="url"],
input[type="text"] {
    padding: 8px 10px;
    background: #111827;
    border: 1px solid #1f2937;
    color: #e5e7eb;
    border-radius: 10px;
    min-width: 220px;
}

button {
    appearance: none;
    border: 1px solid #1f2937;
    background: #111827;
    color: #e5e7eb;
    padding: 8px 12px;
    border-radius: 10px;
    cursor: pointer;
    font-size: 13px;
}

button.primary {
    border-color: transparent;
    background: #6366f1;
    color: #fff;
}

button.ghost {
    background: transparent;
}

button.danger {
    border-color: transparent;
    background: #ef4444;
    color: #fff;
}

.fb-main {
    padding: 14px 16px;
}

.dropzone {
    border: 1px dashed #334155;
    border-radius: 14px;
    padding: 18px;
    text-align: center;
    color: #9ca3af;
    margin-bottom: 14px;
}

.dropzone.drag {
    background: #0b1220;
    color: #cbd5e1;
}

.empty {
    color: #9ca3af;
    text-align: center;
    padding: 40px 10px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 12px;
}

.card {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 14px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.card:hover {
    border-color: #334155;
}

.thumb {
    width: 100%;
    aspect-ratio: 4/3;
    object-fit: cover;
    background: #0b1020;
}

.thumb.ph {
    display: grid;
    place-items: center;
    font-size: 40px;
}

.meta {
    padding: 10px;
    display: grid;
    gap: 8px;
}

.title {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #ffffff;
}

.muted {
    color: #9ca3af;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.row {
    display: flex;
    gap: 8px;
    align-items: center;
}

.ft {
    border-top: 1px solid #1f2937;
    padding: 12px 16px;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
}

.pagination {
    display: flex;
    gap: 6px;
    align-items: center;
}

.pill {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid #1f2937;
}

.loading {
    position: fixed;
    inset: auto 16px 16px auto;
    background: #111827;
    border: 1px solid #1f2937;
    padding: 8px 10px;
    border-radius: 10px;
}

.modal {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .45);
    display: grid;
    place-items: center;
    z-index: 50;
}

.panel {
    width: min(560px, 92vw);
    background: #0b1220;
    border: 1px solid #1f2937;
    border-radius: 16px;
    overflow: hidden;
}

.dlg-hd {
    padding: 12px 14px;
    border-bottom: 1px solid #1f2937;
    font-weight: 600;
}

.dlg-bd {
    padding: 14px;
    display: grid;
    gap: 12px;
}

.dlg-ft {
    padding: 12px 14px;
    border-top: 1px solid #1f2937;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}

.field {
    display: grid;
    gap: 6px;
}

label {
    font-size: 12px;
    color: #cbd5e1;
}

.help {
    color: #9ca3af;
    font-size: 12px;
}

.preview {
    border: 1px solid #1f2937;
    border-radius: 12px;
    padding: 8px;
    background: #0f172a;
}
</style>
